import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  ArrowBackOutlined,
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  CloseOutlined,
  CodeOutlined,
  CopyAllOutlined,
  KeyboardArrowRightOutlined,
  PersonOutline,
  VisibilityOutlined,
} from "@mui/icons-material";
import { blue, grey, yellow } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isObject from "lodash/isObject";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import AgButtonDownloadTable from "../../components/agGrid/AgButtonDownloadTable";
import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";

import {
  Box,
  Button,
  Divider,
  IconButton,
  Switch,
  Typography,
} from "@mui/material";
import { columnDefSuppressFloatingFilterButton } from "../../_src_shared/agGrid/columnDef/columnDefSuppressFloatingFilterButton";
import PrimaryContentCard from "../../_src_shared/components/layout/PrimaryContentCard";
import { useColumnTypes } from "../../_src_shared/utils/agGrid/columnTypes/hooks/useColumnTypes";
import CellRendererAppLink from "../../components/agGrid/cellRenderer/CellRendererAppLink";
import CellRendererDownloadLoanApplication from "../../components/agGrid/cellRenderer/CellRendererDownloadLoanApplication";
import CellRendererLoanFiles from "../../components/agGrid/cellRenderer/CellRendererLoanFiles";
import ApplicationAssignmentModal from "../../components/common/ApplicationAssignmentModal";
import CreateLoanFromAppButton from "../../components/common/buttons/CreateLoanFromAppButton";
import JsonPreview from "../../components/common/JsonPreview";
import LoanFilesDisplayTable from "../../components/common/Loan/tables/LoanFilesDisplayTable";
import RffForm from "../../components/finalForm/RffForm";
import TextInput from "../../components/inputs/TextInput";
import Flx from "../../components/layout/Flx";
import Screen from "../../components/layout/Screen";
import TitledHeaderWithSearch from "../../components/layout/TitledHeaderWithSearch";
import BasicModal from "../../components/modals/BasicModal";
import JsonPreviewModal from "../../components/modals/JsonPreviewModal";
import UrlCopyPastModal from "../../components/modals/UrlCopyPastModal";
import TabContent from "../../components/navigation/TabContent";
import ToggleTabSwitcher from "../../components/navigation/ToggleTabSwitcher";
import PdfDownloadButton from "../../components/pdf/components/PdfDownloadButton";
import PdfPreviewer from "../../components/pdf/components/PdfPreviewer";
import LoanApplicationPDF from "../../components/pdf/LoanApplicationPDF";
import Txt from "../../components/typography/Txt";
import OpenInNewTabButton from "../../components/ui/buttons/OpenInNewTabButton";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import MoreOptionsDropdown from "../../components/ui/menus/MoreOptionsDropdown";
import { useBorrowerSubmissionsHook } from "../../hooks/useBorrowerSubmissionsHook";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { cachedDataItemSet } from "../../redux/actions/cachedDataActions";
import {
  loanPipelineClear,
  loanPipelineSet,
} from "../../redux/actions/loanPipelineActions";
import { colDefFilterMultiSelect } from "../../utils/agGrid/colDefs/colDefFilterMultiSelect";
import { sideBarColumnsFilters } from "../../utils/agGrid/sideBar/sideBarColumnsFilters";
import LoanApplicationFormScreen from "../LoanApplicationFormScreen";
import SectionCard from "../../components/layout/SectionCard";
import RowInput from "../../components/inputs/RowInputs/RowInput";

const modalTypesEnums = {
  PREVIEW: "preview",
  DROPDOWN: "dropdown",
  RUN_REPORT: "runReport",
  FORMSPY: "formSpy",
};
const CACHED_DATA_KEY = "loanApplication";
const TABS = {
  ALL_APPS: "All Loan Applications",
  MY_APPS: "Applications Sent By Me",
  UNASSIGNED: "Unassigned Applications",
};
const LoanApplicationPipelineScreen = () => {
  const ref = useRef();
  const loanApps = useSelector((state) => state?.cachedData?.[CACHED_DATA_KEY]);
  const loanPipeline = useSelector((state) => state?.loanPipeline);
  const user = useSelector((state) => state.user);

  const [quickFilter, setQuickFilter] = useState(null);
  const [tab, setTab] = useState(TABS.ALL_APPS);
  const [showAll, setShowAll] = useState(false);

  const dispatch = useDispatch();
  const { id } = useParams();
  const { getApplications } = useBorrowerSubmissionsHook();
  const { getUserPipeline } = usePipelineHook();
  const navigate = useNavigate();

  const onRowClicked = useCallback((props) => {
    navigate(`/loan-applications/${props.data._id}`);
  }, []);

  const filteredApps = useMemo(() => {
    if (tab === TABS.MY_APPS) {
      return (
        loanApps?.filter(
          (app) => app?.app_link?.link_created_by === user?.emailAddress,
        ) || []
      );
    }

    if (tab === TABS.UNASSIGNED) {
      return loanApps?.filter((app) => isNil(app?.loanNumber)) || [];
    }
    return loanApps;
  }, [tab, loanApps, user]);

  useEffect(() => {
    if (isArray(loanApps)) {
      return;
    }

    if (isNil(loanApps)) {
      getApplications({
        onSuccessFn: (d) => {
          dispatch(cachedDataItemSet(CACHED_DATA_KEY, d));
          // setLastSynced(new Date().toLocaleString());
        },
      });
    }
  }, [loanApps]);

  useEffect(() => {
    if (isArray(loanPipeline)) {
      return;
    }

    if (isNil(loanApps)) {
      getUserPipeline({
        onSuccessFn: (pipeline) => {
          dispatch(loanPipelineSet(pipeline));
          return pipeline;
        },
        onFailFn: () => {
          return [];
        },
      });
    }
  }, [loanPipeline]);

  return (
    <Screen>
      <TitledHeaderWithSearch
        title={"Loan Application Submissions"}
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
        endContent={
          <Flx g={1}>
            {/* <BorrowerApplicationLinksButton>
              Send Loan App
            </BorrowerApplicationLinksButton> */}
            <LoanAppUrlCopyPasteModalButton />
            <AgButtonDownloadTable ref={ref} downloadName="Loan Applications" />
          </Flx>
        }
      />
      <Flx
        sx={{
          borderTop: `1px solid ${grey[300]}`,
          borderBottom: `1px solid ${grey[300]}`,
          py: 0.4,
          px: 0.4,
        }}
        ac
        jb
      >
        <ToggleTabSwitcher
          value={tab}
          onChange={setTab}
          tabs={Object.values(TABS)}
        />

        <Flx ac g={2.4}>
          <Button
            size="small"
            variant={"text"}
            color={showAll ? "primary" : "gray"}
            sx={{ fontWeight: showAll ? 700 : 500 }}
            startIcon={
              showAll ? (
                <CheckBoxOutlined />
              ) : (
                <CheckBoxOutlineBlankOutlined className="thin" />
              )
            }
            onClick={() => setShowAll((s) => !s)}
          >
            Show All Details & Columns
          </Button>
        </Flx>
      </Flx>
      <Flx sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
        <AppSubmissionsTable
          rowData={filteredApps}
          quickFilter={quickFilter}
          onRowClicked={onRowClicked}
        />
        {/* <loanApplicationsTable
          showAll={showAll}
          loading={loading}
          ref={ref}
          rowData={filteredApps}
          onRowClicked={onRowClicked}
          quickFilter={quickFilter}
        /> */}
        {id && (
          <LoanApplicationPreviewPane
            onClose={() => navigate("/loan-applications")}
          />
        )}
      </Flx>
      {/* <Flx sx={{ height: "100%", position: "relative" }} g={1}>
        <AppSubmissionsTable rowData={loanApps} quickFilter={quickFilter} />
      </Flx> */}
    </Screen>
  );
};

const LoanAppUrlCopyPasteModalButton = ({ children }) => {
  const [baseUrl, setBaseUrl] = useState(null);
  const { generateLOApplicationLink } = useBorrowerSubmissionsHook();
  const user = useSelector((state) => state.user);
  const [hideLogo, setHideLogo] = useState(false);
  const [showCustomBranding, setShowCustomBranding] = useState(false);
  const [customBrandingName, setCustomBrandingName] = useState("");
  const [show, setShow] = useState(false);
  const currentUserEmail = useMemo(() => user?.emailAddress, [user]);

  const onClose = () => {
    setBaseUrl(null);
    setShow(false);
    setHideLogo(false);
    setShowCustomBranding(false);
    setCustomBrandingName("");
  };
  useEffect(() => {
    if (isNil(baseUrl) && show) {
      generateLOApplicationLink({
        salesperson: currentUserEmail,
        onSuccessFn: (d) => {
          setBaseUrl(d.link);
        },
      });
    }
  }, [currentUserEmail, show]);

  const url = useMemo(() => {
    if (!baseUrl) return null;

    try {
      const parsedUrl = new URL(baseUrl);

      if (hideLogo) {
        parsedUrl.searchParams.set("hideLogo", "true");
      } else {
        parsedUrl.searchParams.delete("hideLogo");
      }

      if (hideLogo && showCustomBranding && customBrandingName?.trim()) {
        parsedUrl.searchParams.set("brandingName", customBrandingName.trim());
      } else {
        parsedUrl.searchParams.delete("brandingName");
      }

      return parsedUrl.toString();
    } catch (error) {
      return baseUrl;
    }
  }, [baseUrl, hideLogo, showCustomBranding, customBrandingName]);

  return (
    <>
      <Button
        onClick={() => setShow(true)}
        // color={color}
        // variant={variant}
        variant="outlined"
        endIcon={<CopyAllOutlined className="thin" />}
        sx={{
          flexGrow: 1,
        }}
        text={"Assign Existing File"}
      >
        Copy Loan App Link
      </Button>
      {show ? (
        <UrlCopyPastModal
          color="primary"
          title="Loan Application Link"
          url={url}
          show
          onClose={onClose}
          hideButton
          description="Copy and share the following link to allow borrowers to fill out a quick application."
          size="small"
          bodyTopContent={
            <Flx sx={{ mb: 3 }}>
              <SectionCard>
                <RowInput
                  label="Hide Logo"
                  type="switch"
                  description="Remove the logo and branding from the Loan Application form"
                  checked={hideLogo}
                  onChange={() => setHideLogo((s) => !s)}
                />

                {hideLogo && (
                  <>
                    <Divider />
                    <RowInput
                      label="Use Custom Name as Logo"
                      type="switch"
                      description="Remove the logo and branding from the Loan Application form"
                      checked={showCustomBranding}
                      onChange={() => setShowCustomBranding((s) => !s)}
                    />

                    {showCustomBranding && (
                      <>
                        <Divider />
                        <RowInput
                          label="Custom Branding Name"
                          description="Remove or add your own custom branding to the Loan Application form"
                          value={customBrandingName}
                          type="text"
                          onChange={(v) => setCustomBrandingName(v)}
                        />
                      </>
                    )}
                  </>
                )}
              </SectionCard>
            </Flx>
          }
        />
      ) : null}
    </>
  );
};

const LoanApplicationPreviewPane = ({ onClose }) => {
  const { id } = useParams();

  const cachedData = useSelector(
    (state) => state?.cachedData?.[CACHED_DATA_KEY],
  );

  const loanApp = useMemo(() => {
    return cachedData?.find((app) => app._id === id);
  }, [id, cachedData]);

  const TABS = useMemo(() => {
    const BASE_TABS = {
      PREVIEW: "Preview",
      DETAILS: "Details",
      FILES: "Uploaded Files",
      RAW_JSON: "Raw JSON",
    };

    if (!loanApp?.documents || loanApp.documents.length === 0) {
      const { FILES, ...rest } = BASE_TABS;
      return rest;
    }

    return BASE_TABS;
  }, [loanApp?.documents]);

  const [tab, setTab] = useState(TABS.PREVIEW);

  const filenameDownloadName = useMemo(() => {
    if (loanApp?.firstName && loanApp?.lastName) {
      return `${loanApp?.firstName}-${loanApp?.lastName}-loan-application.pdf`;
    }
    return "loan-application-form.pdf";
  }, [loanApp]);

  if (isNil(loanApp)) {
    return;
  }
  return (
    <Flx
      column
      sx={{
        background: grey[200],
        maxWidth: "14vw",
        width: "100%",
        minWidth: "820px",
        // width: "820px",
        overflow: "hidden",
        borderLeft: `2px solid ${grey[400]}`,
      }}
    >
      <Box
        sx={{
          background: "#ffffff",
          flexGrow: 0,
          p: 0.5,
          borderBottom: `1px solid ${grey[400]}`,
        }}
      >
        <Flx fw jb ac>
          <ToggleTabSwitcher
            tabs={Object.values(TABS)}
            value={tab}
            onChange={setTab}
          />
          <IconButton size="small" onClick={onClose}>
            <CloseOutlined />
          </IconButton>
        </Flx>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          height: "100%",
          background: grey[200],
          p: 2,
        }}
      >
        <TabContent activeTab={tab} tabKey={TABS.PREVIEW}>
          <PreviewPaneCard
            title={"Loan Application"}
            bodySx={{
              position: "relative",
              height: "100%",
            }}
            endContent={
              <RffForm suppressFooter initialValues={loanApp}>
                <Flx ac gap={1}>
                  <CreateLoanFromAppButton
                    data={loanApp}
                    confirmationDialogTitle="Create Loan From Application"
                    typeKeyName={"loanApplication"}
                  />
                  {/* <Tooltip title={"Work In Progress, does not work yet"}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<AssignmentReturnOutlined className="thin" />}
                    >
                      Push To Existing Loan
                    </Button>
                  </Tooltip> */}
                  <PdfDownloadButton filename={filenameDownloadName}>
                    <LoanApplicationPDF
                      formData={loanApp}
                      title="Loan Application"
                    />
                  </PdfDownloadButton>
                </Flx>
              </RffForm>
            }
          >
            <PdfPreviewer
              sx={{
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                minHeight: "720px",
              }}
            >
              <LoanApplicationPDF formData={loanApp} title="Loan Application" />
            </PdfPreviewer>
          </PreviewPaneCard>
        </TabContent>

        <TabContent activeTab={tab} tabKey={TABS.FILES}>
          <PreviewPaneCard title={"Uploaded Application Files"}>
            <LoanFilesDisplayTable rowData={loanApp?.documents} />
          </PreviewPaneCard>
        </TabContent>
        <TabContent activeTab={tab} tabKey={TABS.RAW_JSON}>
          <PreviewPaneCard
            title={"Raw Application JSON"}
            cardSx={{ overflowY: "auto" }}
          >
            <JsonPreview defaultCollapse={4} values={loanApp} />
          </PreviewPaneCard>
        </TabContent>
      </Box>
    </Flx>
  );
};

const PreviewPaneCard = ({
  title,
  subTitle,
  children,
  cardSx,
  bodySx,
  endContent,
}) => {
  return (
    <PrimaryContentCard
      title={title}
      subTitle={subTitle}
      endContent={endContent}
      cardSx={{ mb: 0, flexGrow: 1, height: "100%", ...cardSx }}
      bodySx={{
        position: "relative",
        height: "100%",
        ...bodySx,
      }}
    >
      {children}
    </PrimaryContentCard>
  );
};

// =================================== ORIGINAL ==================================

const LoanAppActionsDropdown = ({ params }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, createLoanFromApplication } = useUnderwritingHook();

  const applicationData = useMemo(() => params?.data, [params?.data]);

  const [assignedApplication, setAssignedApplication] = useState(null);

  const [modal, setModal] = useState(false);

  const onAssignSelected = useCallback(() => {
    setAssignedApplication(applicationData);
  }, [applicationData]);

  const clearAssignedSelect = useCallback(() => {
    setAssignedApplication(null);
  }, []);

  const items = useMemo(
    () => [
      // {
      //   label: "Create Loan From Application",
      //   icon: <ConstructionOutlined fontSize="small" />,
      //   onClick: () => setModal(modalTypesEnums.DROPDOWN),
      // },
      {
        label: "Assign To Loan or Salesperson",
        icon: <PersonOutline fontSize="small" />,
        onClick: onAssignSelected,
      },
      {
        label: "Preview Application",
        icon: <VisibilityOutlined fontSize="small" />,
        onClick: () => setModal(modalTypesEnums.PREVIEW),
      },
      {
        label: "View Raw App Data",
        icon: <CodeOutlined className="thin" fontSize="small" />,

        onClick: () => setModal(modalTypesEnums.FORMSPY),
      },
    ],
    [onAssignSelected, setModal],
  );

  // if (!modal && isNil(assignedApplication)) {
  //   return null;
  // }

  return (
    <>
      <MoreOptionsDropdown
        title={"Application Actions"}
        tooltip="Application Actions"
        items={items}
      />
      <JsonPreviewModal
        title="Loan Application Preview"
        values={modal === modalTypesEnums.FORMSPY ? applicationData : null}
        show={modal === modalTypesEnums.FORMSPY}
        defaultCollapse={1}
        onClose={() => setModal(false)}
      />
      {isNil(assignedApplication) ? null : (
        <ApplicationAssignmentModal
          application={assignedApplication}
          type={"loanApps"}
          onClearSelected={clearAssignedSelect}
        />
      )}
      <CreateLoanFromLoanAppModal
        application={applicationData}
        onClose={() => setModal(false)}
        show={modal === modalTypesEnums.DROPDOWN}
        // title="Create Loan From Application"
        // message="Are you sure you want to create a loan from this application?"
        // cancelText="Cancel"
        // confirmText="Create Loan"
        // loading={loading}
        // onConfirm={() => {
        //   onCreateLoanClick();
        //   setModal(null);
        // }}
      />
      {/* <ConfirmationDialog
        open={modal === modalTypesEnums.DROPDOWN}
        title="Create Loan From Application"
        message="Are you sure you want to create a loan from this application?"
        cancelText="Cancel"
        confirmText="Create Loan"
        loading={loading}
        onConfirm={() => {
          onCreateLoanClick();
          setModal(null);
        }}
        onCancel={() => setModal(false)}
      /> */}
      <PreviewModalDialog
        show={modal === modalTypesEnums.PREVIEW}
        application={applicationData}
        onCancel={() => setModal(null)}
      />
    </>
  );
};

const CreateLoanFromLoanAppModal = ({ show, application, onClose }) => {
  const { loading, createLoanFromApplication } = useUnderwritingHook();
  const [success, setSuccess] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onCreateLoanClick = useCallback(() => {
    const appId = application?._id;

    console.log("Create loan from application:", { appId });

    // dispatch(loanPipelineClear());
    // dispatch(cachedDataItemSet("loanApps", null));
    // setSuccess(SUCCESS_DATA);
    // return;
    createLoanFromApplication({
      applicationId: appId,
      onSuccessFn: (d) => {
        setSuccess(SUCCESS_DATA);
        dispatch(cachedDataItemSet("loanApps", null));
        dispatch(loanPipelineClear());
        // navigate(`/loans`);
      },
    });
  }, [application?._id, createLoanFromApplication, dispatch]);

  const handleOnClose = useCallback((props) => {
    setSuccess(false);
    onClose();
  }, []);

  if (!show) {
    return null;
  }

  if (success) {
    return (
      <BasicModal
        maxWidth="sm"
        show={show}
        title="Loan Created Successfully"
        message="The loan has been created successfully."
        loading={loading}
        onConfirm={onCreateLoanClick}
        onCancel={handleOnClose}
      >
        <Txt secondary>Loan Details:</Txt>
        <Box sx={{ background: grey[100], p: 2, borderRadius: 1 }}>
          <Flx g={1.5} column>
            <Flx gap={1} ac>
              <Txt>Loan #:</Txt>
              <Txt bold>{success?.loanNumber}</Txt>
            </Flx>
            <Flx gap={1} ac>
              <Txt>Name:</Txt>
              <Txt bold>{success?.loanName}</Txt>
            </Flx>
            <Flx gap={1} ac>
              <Txt>Product Type:</Txt>
              <Txt bold>{success?.loanProductType}</Txt>
            </Flx>
            <Flx gap={1} ac>
              <Txt>Purpose:</Txt>
              <Txt bold>{success?.loanPurpose}</Txt>
            </Flx>
          </Flx>
        </Box>
        <Flx
          g={1}
          jb
          ac
          fw
          sx={{
            mt: 4,
            ".txt, .MuiButtonBase-root": {
              fontSize: "12px",
            },
          }}
        >
          <Button
            variant="outlined"
            onClick={handleOnClose}
            startIcon={<ArrowBackOutlined className="thin" />}
          >
            Back to Loan Applications
          </Button>
          <Flx ac jb g={2}>
            <OpenInNewTabButton linkIcon to={`/loan/${success?.loanNumber}`}>
              <Txt>Open in New Tab</Txt>
            </OpenInNewTabButton>
            <Button
              onClick={() => navigate(`/loan/${success?.loanNumber}`)}
              endIcon={<KeyboardArrowRightOutlined className="thin" />}
            >
              Go to Loan {success?.loanNumber}
            </Button>
          </Flx>
        </Flx>
      </BasicModal>
    );
  }
  return (
    <ConfirmationDialog
      open={show}
      title="Create Loan From Application"
      message="Are you sure you want to create a loan from this application?"
      cancelText="Cancel"
      confirmText="Yes, Create New Loan"
      loading={loading}
      onConfirm={onCreateLoanClick}
      onCancel={handleOnClose}
    />
  );
};

const PreviewModalDialog = ({ show, application, onCancel }) => {
  if (!show) {
    return null;
  }
  return (
    <BasicModal
      maxWidth={"xl"}
      title={"Application Preview"}
      show={show}
      allowFullScreen
      onClose={onCancel}
      suppressBodyPadding
    >
      <LoanApplicationFormScreen initialValues={application} />
    </BasicModal>
  );
};

const AppSubmissionsTable = ({ quickFilter, rowData, onRowClicked }) => {
  const loanPipeline = useSelector((state) => state?.loanPipeline);
  const { id } = useParams();
  const cellStyleUnassigned = useCallback((p) => {
    if (isNil(p.value) || p.value === "Unassigned") {
      return {
        background: yellow[50],
        color: grey[500],
      };
    }
  }, []);
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Actions",
        // pinned: "left",
        width: 90,
        resizable: false,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          paddingLeft: "6px",
        },
        suppressHeaderFilterButton: true,
        suppressFloatingFilterButton: true,
        floatingFilter: false,
        cellRenderer: (params) => {
          return (
            <Flx ac>
              <LoanAppActionsDropdown params={params} />
              <CellRendererDownloadLoanApplication {...params} />
            </Flx>
          );
        },
      },
      {
        headerName: "Assigned Loan",
        // pinned: "left",
        width: 130,
        cellClass: "ag-cell-vertical-center",
        valueGetter: (p) => {
          const dealId = p?.data?.dealId;
          if (isNil(dealId)) {
            return null;
          }
          if (isNil(loanPipeline)) {
            return "Loading...";
          }
          const loanNumber = loanPipeline?.find(
            (d) => d?._id === dealId,
          )?.loanNumber;
          if (isNil(loanNumber)) {
            return null;
          }
          return loanNumber;
        },
        cellRenderer: (params) => {
          const value = params?.value;
          if (isNil(value) || value === "Loading...") {
            return "Unassigned";
          }
          return (
            <OpenInNewTabButton linkIcon to={`/loan/${value}`}>
              {value}
            </OpenInNewTabButton>
          );
        },
        cellStyle: cellStyleUnassigned,
        ...columnDefSuppressFloatingFilterButton,
      },
      {
        headerName: "Assigned Salesperson",
        field: "salesperson",
        width: 260,
        cellClass: "ag-cell-vertical-center",
        // pinned: "left",
        valueFormatter: (p) => {
          if (isNil(p.value)) {
            return "Unassigned";
          }
          if (isObject(p.value)) {
            return p.value?.fullName;
          }

          return p?.value;
        },

        cellStyle: cellStyleUnassigned,
        ...columnDefSuppressFloatingFilterButton,
      },
      {
        headerName: "Assigned Loan",
        field: "dealId",
        // pinned: "left",
        width: 125,
        hide: true,
      },
      {
        headerName: "Assigned By",
        hide: true,
        field: "assigned_by",
        // pinned: "left",
      },

      {
        headerName: "Submission Date",
        type: "date",
        field: "submission_date",
        sort: "desc",
      },
      { hide: true, headerName: "_id", field: "_id" },

      {
        headerName: "Borrower Name",
        field: "fullName",

        filter: "agTextColumnFilter",
        valueGetter: (params) => {
          return `${params.data?.firstName || ""} ${params.data?.lastName || ""}`;
        },
      },
      {
        field: "documents",
        headerName: "Uploaded Files",
        cellRenderer: CellRendererLoanFiles,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          padding: "0 8px",
        },
      },
      {
        headerName: "Sent Application Link",
        field: "app_link",
        width: 350,
        flexGrow: 1,
        autoHeight: true,
        cellRenderer: (p) => (
          <CellRendererAppLink params={p} loanPipeline={loanPipeline} />
        ),
      },
      {
        headerName: "Phone",
        field: "phone",

        filter: "agTextColumnFilter",
        valueFormatter: (params) => {
          const phone = params.value;
          if (!phone) return "";
          return phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
        },
      },
      {
        headerName: "Email",
        field: "emailAddress",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Citizenship",
        field: "citizenshipStatus",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "DOB",
        field: "dateOfBirth",
        filter: "agDateColumnFilter",
        type: "date",
      },
      {
        headerName: "FICO",
        field: "estimatedFICO",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Marital Status",
        field: "maritalStatus",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Property Address",
        field: "subjectProperties",
        filter: "agTextColumnFilter",
        valueGetter: (params) => {
          if (isEmpty(params.data?.subjectProperties)) return "None";
          return (
            params.data?.subjectProperties[0]?.address?.fullAddress || "None"
          );
        },
      },
      {
        headerName: "Property Type",
        field: "propertyType",
        filter: "agTextColumnFilter",
        valueGetter: (params) => {
          if (isEmpty(params.data?.subjectProperties)) return "";
          return params.data?.subjectProperties[0]?.propertyType || "";
        },
      },
      {
        headerName: "Purchase Price",
        field: "purchasePrice",
        filter: "agNumberColumnFilter",
        valueGetter: (params) => {
          if (isEmpty(params.data?.subjectProperties)) return null;
          return params.data?.subjectProperties[0]?.purchasePrice || null;
        },
        type: "dollar",
      },
      {
        headerName: "ARV",
        field: "arv",
        filter: "agNumberColumnFilter",
        valueGetter: (params) => {
          if (isEmpty(params.data?.subjectProperties)) return null;
          return params.data?.subjectProperties[0]?.arv || null;
        },
        type: "dollar",
      },
      {
        headerName: "As-Is Value",
        field: "asIsValue",
        filter: "agNumberColumnFilter",
        valueGetter: (params) => {
          if (isEmpty(params.data?.subjectProperties)) return null;
          return params.data?.subjectProperties[0]?.asIsValue || null;
        },
        type: "dollar",
      },
      {
        headerName: "Annual Taxes",
        field: "annualTaxes",
        filter: "agNumberColumnFilter",
        valueGetter: (params) => {
          if (isEmpty(params.data?.subjectProperties)) return null;
          return params.data?.subjectProperties[0]?.annualTaxes || null;
        },
        type: "dollar",
      },
      {
        headerName: "Annual Insurance",
        field: "annualInsurance",
        filter: "agNumberColumnFilter",
        valueGetter: (params) => {
          if (isEmpty(params.data?.subjectProperties)) return null;
          return params.data?.subjectProperties[0]?.annualInsurance || null;
        },
        type: "dollar",
      },
      {
        headerName: "Base Loan Amount",
        field: "baseLoanAmount",
        filter: "agNumberColumnFilter",
        type: "dollar",
      },
      {
        headerName: "Total Holdback",
        field: "totalHoldback",
        filter: "agNumberColumnFilter",
        type: "dollar",
      },
      {
        headerName: "Cash Liquidity",
        field: "cashLiquidity",
        filter: "agNumberColumnFilter",
        type: "dollar",
      },
      {
        headerName: "Other Liquidity",
        field: "otherLiquidity",
        filter: "agNumberColumnFilter",
        type: "dollar",
      },
      {
        headerName: "Loan Product Type",
        field: "loanProductType",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Loan Purpose",
        field: "loanPurpose",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Expected Closing",
        field: "expectedClosingDate",
        filter: "agDateColumnFilter",
        type: "date",
      },
      {
        headerName: "Loan Salesperson",
        field: "loanSalesperson",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Property Condition",
        field: "propertyCondition",
        filter: "agTextColumnFilter",
        valueGetter: (params) => {
          if (isEmpty(params.data?.subjectProperties)) return "";
          return (
            params.data?.subjectProperties[0]?.ffPropertyStatus ||
            params.data?.subjectProperties[0]?.gucPropertyStatus ||
            ""
          );
        },
      },
      {
        headerName: "Fix & Flip Experience",
        field: "hasExperienceWithFixFlips",
        filter: "agSetColumnFilter",
        valueFormatter: (params) => (params.value ? "Yes" : "No"),
      },
      {
        headerName: "# of Fix & Flips",
        field: "fixFlipsWithinTwoYears",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Entity Name",
        field: "entityName",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Entity Type",
        field: "entityType",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Entity State",
        field: "entityStateOfOrganization",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Primary Address",
        field: "primaryAddress.fullAddress",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Flood Zone",
        field: "floodZone",
        filter: "agSetColumnFilter",
        valueGetter: (params) => {
          if (isEmpty(params.data?.subjectProperties)) return false;
          return params.data?.subjectProperties[0]?.floodZone || false;
        },
        valueFormatter: (params) => (params.value ? "Yes" : "No"),
      },
    ];
  }, [loanPipeline, cellStyleUnassigned]);

  const getRowStyle = useCallback(
    (props) => {
      if (props?.data?._id === id) {
        return {
          background: blue[50],
        };
      }
    },
    [id],
  );

  const defaultColDef = useMemo(() => {
    return {
      // enableRowGroup: true,
      floatingFilter: true,
      filter: colDefFilterMultiSelect,
      showRowGroup: false,
      suppressHeaderFilterButton: true,
      suppressMenu: true,
      cellClass: "ag-cell-vertical-center",
    };
  }, []);

  const columnTypes = useColumnTypes();
  const sideBar = useMemo(() => sideBarColumnsFilters(), []);

  return (
    <AgGridTableWrapper height={"100%"} noBorder>
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        quickFilterText={quickFilter}
        defaultColDef={defaultColDef}
        getRowStyle={getRowStyle}
        onRowClicked={onRowClicked}
        getRowId={(params) => params.data._id}
        // groupDisplayType={"groupRows"}
        columnTypes={columnTypes}
        loading={isNil(rowData)}
        sideBar={sideBar}
      />
    </AgGridTableWrapper>
  );
};

const SUCCESS_DATA = {
  _id: "6970fa22fad2f8372a78197a",
  createDateTime: "2026-01-21T16:09:06.515986",
  updateDateTime: "2026-01-21T16:09:06.515931",
  lastViewDateTime: "2026-01-21T16:09:06.515990",
  pipeline: "Underwriting",
  loanNumber: 1114,
  loanName: "LQ27038 - 1245 Lenox Ave, Miami Beach, FL 33139 - RTL (P,U,C)",
  isTableFunding: false,
  isBalanceSheetClosing: false,
  loanStatus: "Prospect",
  loanProductType: "Stabilized Bridge",
  loanPurpose: "Purchase",
  exitStrategy: "Increase rent and sell",
  salesperson: "dev1@arrowservicing.com",
  permittedUsers: ["dev2@arrowservicing.com", "dev1@arrowservicing.com"],
  baseLoanAmount: 0,
  totalHoldback: 0,
  dutchInterest: false,
  subjectProperties: [
    {
      _id: "6970fa22fad2f8372a78197b",
      address: {
        fullAddress: "1450 Brickell Avenue, Miami, FL 33131",
        streetName: "Brickell Avenue",
        streetNumber: "1450",
        zip: "33131",
        county: "FL",
        state: "Florida",
        city: "Miami",
        country: "United States",
        latitude: 25.7589108,
        longitude: -80.1928818,
        id: "Ro29DBf51dv3m1yksamqLQ-1755505011096",
        countryCode: "US",
        coordinateTuple: [25.7589108, -80.1928818],
        score: 0.9513721265463289,
        streetAddress: "1450 Brickell Avenue",
      },
      propertyType: "SFR",
      propertyRuralIndicator: false,
      scopeOfWork: [],
      units: [],
      costBasis: 0,
      secondaryAsIsVariance: 0,
      secondaryArvVariance: 0,
      unitCount: 0,
      vacantUnitCount: 0,
      residentialUnitCount: 0,
      commercialUnitCount: 0,
      monthlyTaxes: 0,
      monthlyInsurance: 0,
      monthlyFloodInsurance: 0,
      monthlyHoaFees: 0,
      monthlyPropertyMgmtFees: 0,
      monthlyOtherExpense: 0,
      squareFootageChange: 0,
    },
  ],
  borrowers: [
    {
      _id: "6970fa22fad2f8372a78197c",
      firstName: "Test",
      lastName: "Test",
      phone: "5162205739",
      emailAddress: "dev1@arrowservicing.com",
      createDateTime: "2026-01-20T00:30:47.172076",
      signUpDate: "2026-01-20T00:30:47.172139",
      dealId: "687e3b45138dca9647e7ffb1",
      ficoLow: 0,
      ficoMid: 0,
      ficoMax: 0,
      otherLiquidity: 0,
      cashLiquidity: 0,
      borrowerSSN: "000000000",
      primaryAddress: {
        fullAddress: "1450 Brickell Avenue, Miami, FL 33131",
        streetName: "Brickell Avenue",
        streetNumber: "1450",
        zip: "33131",
        county: "FL",
        state: "Florida",
        city: "Miami",
        country: "United States",
        latitude: 25.7589108,
        longitude: -80.1928818,
        id: "Ro29DBf51dv3m1yksamqLQ-1755504956354",
        countryCode: "US",
        coordinateTuple: [25.7589108, -80.1928818],
        score: 1,
        streetAddress: "1450 Brickell Avenue",
      },
      entityPercentOwnership: 0,
      isGuarantor: false,
      isPrimaryContactForLoan: false,
      maritalStatus: "Married",
      firstTimeHomebuyer: false,
      hasExperienceWithIncomeProducingProperties: false,
      hasExperienceWithFixFlips: false,
      hasExperienceWithGroundUpConstruction: false,
      citizenshipStatus: "US Citizen",
      dateOfBirth: "1999-01-01T05:00:00+00:00",
      verifiedLiquidity: 0,
      verifiedExperienceTotal: 0,
      borrowerGuidelineChecks: [],
      profileComplete: true,
      email_lower: "dev1@arrowservicing.com",
      email_domain: "arrowservicing.com",
      totalLiquidity: 0,
      foreignNationalFlag: false,
    },
  ],
  updateHistory: [],
  termMonths: 0,
  interestOnlyMonths: 0,
  expectedClosingDate: "2026-03-03T00:30:47.628401",
  interestRate: 0,
  discountPoints: 0,
  originationPoints: 0,
  interestCalcType: "30/360",
  outstandingLoanUpb: 0,
  existingLoanInDefault: false,
  applications: ["68a2e1879edd1cc745098fbd"],
  createdBy: "dev2@arrowservicing.com",
  createdFrom: "loanApplication",
  createdFromLoanApp: "68a2e1879edd1cc745098fbd",
  totalLoanAmount: 0,
  _property_totals: {
    purchasePrice: 0,
    asIsValue: 0,
    arv: 0,
    secondaryAsIsValue: 0,
    secondaryArv: 0,
    costBasis: 0,
    assignmentFees: 0,
    annualTaxes: 0,
    annualInsurance: 0,
    annualFloodInsurance: 0,
    annualHoaFees: 0,
    annualPropertyMgmtFees: 0,
    annualOtherExpense: 0,
    constructionBudget: 0,
    interestReserves: 0,
  },
  totalPurchasePrice: 0,
  totalAsIsValue: 0,
  totalArv: 0,
  totalSecondaryAsIsValue: 0,
  totalSecondaryArv: 0,
  totalCostBasis: 0,
  totalAssignmentFees: 0,
  totalAnnualTaxes: 0,
  totalAnnualInsurance: 0,
  totalAnnualFloodInsurance: 0,
  totalAnnualHoaFees: 0,
  totalAnnualPropertyMgmtFees: 0,
  totalAnnualOtherExpense: 0,
  totalConstructionBudget: 0,
  totalInterestReserves: 0,
  baseLoanToAsIsValue: 0,
  baseLoanToCost: 0,
  baseLoanToPurchasePrice: 0,
  totalLoanToArv: 0,
  firstPaymentDate: "2026-05-01T00:00:00",
  maturityDate: "2026-04-01T00:00:00",
};
export default LoanApplicationPipelineScreen;
